import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';

export interface ApiInfo {
  name: string;
  path: string;
  hasConfig: boolean;
}

export interface FetchConfigResponse {
  config: any;
  sha: string;
}

export interface FetchDefinitionResponse {
  content: string;
  sha: string;
}

export interface UpdateConfigRequest {
  owner: string;
  repo: string;
  path: string;
  config: any;
  branch?: string;
  message?: string;
}

export interface UpdateDefinitionRequest {
  owner: string;
  repo: string;
  path: string;
  content: string;
  branch?: string;
  message?: string;
}

export interface UpdateConfigResponse {
  commitUrl?: string;
  pullRequestUrl?: string;
  branchName: string;
  commitSha?: string;
  savedContent?: string;
  /** Server skipped creating a commit because content was unchanged */
  skippedNoop?: boolean;
}

export class ConfigEditorClient {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  async fetchConfig(
    owner: string,
    repo: string,
    path: string,
    branch?: string,
    options?: RequestInit,
  ): Promise<FetchConfigResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('config-editor');
    let url = `${baseUrl}/config?owner=${encodeURIComponent(owner)}&repo=${encodeURIComponent(
      repo,
    )}&path=${encodeURIComponent(path)}`;

    if (branch) {
      url += `&branch=${encodeURIComponent(branch)}`;
    }

    const response = await this.fetchApi.fetch(url, options);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch configuration');
    }

    return await response.json();
  }

  async fetchDefinition(
    owner: string,
    repo: string,
    path: string,
    branch?: string,
    options?: RequestInit,
  ): Promise<FetchDefinitionResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('config-editor');
    let url = `${baseUrl}/definition?owner=${encodeURIComponent(owner)}&repo=${encodeURIComponent(
      repo,
    )}&path=${encodeURIComponent(path)}`;

    if (branch) {
      url += `&branch=${encodeURIComponent(branch)}`;
    }

    const response = await this.fetchApi.fetch(url, options);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch definition');
    }

    return await response.json();
  }

  /**
   * Run the canonical config.json transform on the backend and return the formatted
   * object. Pure — no GitHub write. The transform lives in config-editor-common and is
   * shared with the backend, so the UI does not carry its own copy.
   */
  async formatConfig(config: any, templateName?: string): Promise<any> {
    const baseUrl = await this.discoveryApi.getBaseUrl('config-editor');
    const url = `${baseUrl}/format`;

    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ config, templateName }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to format configuration');
    }

    const data = await response.json();
    return data.config;
  }

  async updateConfig(request: UpdateConfigRequest): Promise<UpdateConfigResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('config-editor');
    const url = `${baseUrl}/config`;

    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update configuration');
    }

    return await response.json();
  }

  async updateDefinition(request: UpdateDefinitionRequest): Promise<UpdateConfigResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('config-editor');
    const url = `${baseUrl}/definition`;

    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update definition');
    }

    return await response.json();
  }

  async listApis(
    owner: string,
    repo: string,
    basePath: string,
    branch?: string,
  ): Promise<ApiInfo[]> {
    const baseUrl = await this.discoveryApi.getBaseUrl('config-editor');
    let url = `${baseUrl}/apis?owner=${encodeURIComponent(owner)}&repo=${encodeURIComponent(
      repo,
    )}&basePath=${encodeURIComponent(basePath)}`;

    if (branch) {
      url += `&branch=${encodeURIComponent(branch)}`;
    }

    const response = await this.fetchApi.fetch(url);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to list APIs');
    }

    return await response.json();
  }

  async listBranches(owner: string, repo: string, configItemName?: string): Promise<string[]> {
    const baseUrl = await this.discoveryApi.getBaseUrl('config-editor');
    let url = `${baseUrl}/branches?owner=${encodeURIComponent(owner)}&repo=${encodeURIComponent(
      repo,
    )}`;

    if (configItemName) {
      url += `&configItemName=${encodeURIComponent(configItemName)}`;
    }

    const response = await this.fetchApi.fetch(url);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to list branches');
    }

    return await response.json();
  }

  async getPullRequestUrl(owner: string, repo: string, branch: string): Promise<string | null> {
    const baseUrl = await this.discoveryApi.getBaseUrl('config-editor');
    const url = `${baseUrl}/pr-url?owner=${encodeURIComponent(owner)}&repo=${encodeURIComponent(
      repo,
    )}&branch=${encodeURIComponent(branch)}`;

    const response = await this.fetchApi.fetch(url);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch PR URL');
    }

    const data = await response.json();
    return data.pullRequestUrl ?? null;
  }
}
